import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
    GeneralButton
} from 'sharedComponents';
import './WizardActions.scss';

/**
 * WizardActions - Navigation and action buttons for the wizard
 * Provides consistent navigation interface with customizable button states
 */
const WizardActions = ({
    canGoNext = false,
    canGoPrevious = false,
    canSave = false,
    isFirstStep = false,
    isLastStep = false,
    onNext = () => { },
    onPrevious = () => { },
    onCancel = () => { },
    onReset = () => { },
    onSave = () => { }, // New: Handler for save in edit mode
    submitLabel,
    previousLabel,
    nextLabel,
    cancelLabel,
    resetLabel,
    saveLabel, // New: Custom save label
    showCancel = true,
    showReset = false,
    isLoading = false,
    isEditMode = false, // New: Flag to indicate edit mode
    className = ''
}) => {
    const { t: translate } = useTranslation();

    // Use translation keys as defaults if no custom labels provided
    const finalSubmitLabel = submitLabel ||
        (isEditMode ?
            translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardActions_UpdateLabel') || 'Update' :
            translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardActions_SubmitLabel') || 'Create'
        );
    const finalPreviousLabel = previousLabel || translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardActions_PreviousLabel');
    const finalNextLabel = nextLabel || translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardActions_NextLabel');
    const finalCancelLabel = cancelLabel || translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardActions_CancelLabel');
    const finalResetLabel = resetLabel || translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardActions_ResetLabel');
    const finalSaveLabel = saveLabel || translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardActions_SaveLabel') || 'Save';

    const handleKeyPress = (event, action) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            action();
        }
    };

    const handleNextClick = () => {
        onNext();
    };

    return (
        <div className={`wizard-actions ${className}`}>
            <div className='actions-left'>
                {!isFirstStep &&
                    <GeneralButton
                        type='normal'
                        onClick={onPrevious}
                        onKeyPress={(e) => handleKeyPress(e, onPrevious)}
                        disabled={!canGoPrevious || isLoading}
                        text={finalPreviousLabel}
                    />
                }

                {showReset &&
                    <GeneralButton
                        type='normal'
                        onClick={onReset}
                        onKeyPress={(e) => handleKeyPress(e, onReset)}
                        disabled={isLoading}
                        text={finalResetLabel}
                    />
                }
            </div>

            <div className='actions-right'>
                {showCancel &&
                    <GeneralButton type='normal' text={finalCancelLabel} onClick={onCancel} disabled={isLoading} />
                }

                {/* Edit Mode: Show Save button on all steps */}
                {isEditMode && !isLastStep &&
                    <GeneralButton
                        type='primary'
                        onClick={() => {
                            onSave();
                        }}
                        onKeyPress={(e) => handleKeyPress(e, onSave)}
                        disabled={!canSave || isLoading}
                        text={isLoading ? translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardActions_SavingLabel') || 'Saving...' : finalSaveLabel}
                    />
                }

                {/* Create Mode or Navigation Buttons */}
                {!isEditMode && !isLastStep &&
                    <GeneralButton
                        type='primary'
                        onClick={handleNextClick}
                        onKeyPress={(e) => handleKeyPress(e, handleNextClick)}
                        disabled={!canGoNext || isLoading}
                        text={isLoading ? translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardActions_ProcessingLabel') : finalNextLabel}
                    />
                }

                {/* Last Step: Show appropriate submit button */}
                {isLastStep &&
                    <GeneralButton
                        type='primary'
                        onClick={handleNextClick}
                        onKeyPress={(e) => handleKeyPress(e, handleNextClick)}
                        disabled={!canGoNext || isLoading}
                        text={isLoading ?
                            isEditMode ?
                                translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardActions_UpdatingLabel') || 'Updating...' :
                                translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardActions_SubmittingLabel') || 'Creating...' :
                            finalSubmitLabel
                        }
                    />
                }
            </div>
        </div>
    );
};

WizardActions.propTypes = {
    canGoNext: PropTypes.bool,
    canGoPrevious: PropTypes.bool,
    canSave: PropTypes.bool,
    isFirstStep: PropTypes.bool,
    isLastStep: PropTypes.bool,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func,
    onCancel: PropTypes.func,
    onReset: PropTypes.func,
    submitLabel: PropTypes.string,
    previousLabel: PropTypes.string,
    nextLabel: PropTypes.string,
    cancelLabel: PropTypes.string,
    resetLabel: PropTypes.string,
    showCancel: PropTypes.bool,
    showReset: PropTypes.bool,
    isLoading: PropTypes.bool,
    className: PropTypes.string
};

export default WizardActions;
